import React from 'react';

/**
 * EmptyState - Reusable empty state component
 * 
 * @param {Object} props - Component props
 * @param {string} [props.iconClass=''] - Font Awesome icon class (e.g., 'fa-solid fa-users-slash')
 * @param {string} [props.iconSize='2.5rem'] - Size of the icon
 * @param {string} [props.title=''] - Main title text
 * @param {string} [props.message=''] - Description message
 * @param {string} [props.subMessage=''] - Additional sub message
 * @param {string} [props.className=''] - Additional CSS classes for container
 * @param {Object} [props.style={}] - Additional inline styles for container
 * @param {string} [props.variant='card'] - Variant: 'card', 'inline', 'center'
 * @returns {React.ReactElement} Empty state component
 */
const EmptyState = ({
  iconClass = '',
  iconSize = '2.5rem',
  title = '',
  message = '',
  subMessage = '',
  className = '',
  style = {},
  variant = 'card'
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'inline':
        return {
          display: 'inline-block',
          padding: '0.75rem 1rem',
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
          fontStyle: 'italic'
        };
      case 'center':
        return {
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--text-secondary)',
          fontSize: '0.85rem'
        };
      case 'card':
      default:
        return {
          textAlign: 'center',
          padding: '3rem',
          color: 'var(--text-secondary)'
        };
    }
  };

  const baseStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    ...getVariantStyles(),
    ...style
  };

  return (
    <div className={className} style={baseStyle}>
      {iconClass && (
        <i className={iconClass} style={{ fontSize: iconSize, opacity: 0.3, marginBottom: variant === 'inline' ? 0 : '1rem' }}></i>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {title && (
          <h3 style={{ 
            fontSize: variant === 'inline' ? '0.85rem' : '1.1rem', 
            fontWeight: 600, 
            color: 'var(--text-primary)',
            margin: variant === 'inline' ? 0 : '0 0 0.25rem 0'
          }}>
            {title}
          </h3>
        )}
        {message && (
          <p style={{ 
            fontSize: variant === 'inline' ? '0.85rem' : '0.9rem', 
            margin: 0,
            color: 'var(--text-secondary)'
          }}>
            {message}
          </p>
        )}
        {subMessage && (
          <p style={{ 
            fontSize: '0.85rem', 
            margin: 0,
            color: 'var(--text-secondary)',
            fontStyle: 'italic'
          }}>
            {subMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default EmptyState;